// src/pages/public/ServiceHubPage.tsx
import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { useSeoInventory, usePublicDirectory } from './usePublicSeoData';
import { getSeoContext } from '../../seo/seoContext';
import { buildServiceHubModel } from '../../seo/core/pageModels';
import { resolveServiceCategory } from '../../seo/core/taxonomy';
import { analyzeFacetedUrl, applyFacetsToMetadata } from '../../seo/core/facets';
import { ROUTES } from '../../seo/core/urls';
import type { PublicPageModel } from '../../seo/types';

/**
 * `/services/:serviceSlug` — national hub for one real service category.
 *
 * The slug set is the existing marketplace taxonomy from
 * `backend/src/models/Service.js`; an unknown or legacy-aliased slug redirects
 * to its canonical spelling (or to `/services`) rather than rendering a
 * duplicate URL.
 */
function ServiceHubPage() {
  const ctx = getSeoContext();
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const [searchParams] = useSearchParams();
  const category = resolveServiceCategory(serviceSlug);
  const { inventory, loading: inventoryLoading } = useSeoInventory();

  const { directory, loading: directoryLoading } = usePublicDirectory(
    { serviceCategory: category?.slug, limit: 24 },
    Boolean(category)
  );

  const cities = useMemo(() => {
    if (!category) return [];
    const rows: Array<{ city: string; state: string; stylistCount: number }> = [];
    (inventory?.locations || []).forEach((row) => {
      const match = (row.services || []).find((s) => s.serviceCategory === category.slug);
      if (match) rows.push({ city: row.city, state: row.state, stylistCount: match.stylistCount });
    });
    return rows.sort((a, b) => b.stylistCount - a.stylistCount).slice(0, 30);
  }, [inventory, category]);

  const model = useMemo<PublicPageModel | null>(() => {
    if (!category) return null;
    const national = (inventory?.services || []).find(
      (row) => row.serviceCategory === category.slug
    );

    const built = buildServiceHubModel(
      {
        serviceCategory: category.slug,
        stylistCount: national?.stylistCount ?? directory.total,
        cityCount: cities.length,
        priceMin: directory.priceMin,
        priceMax: directory.priceMax,
        stylists: directory.stylists,
        cities,
      },
      ctx
    ) as PublicPageModel | null;
    if (!built) return null;

    const analysis = analyzeFacetedUrl(ROUTES.service(category.slug), searchParams, {
      baseIndexable: built.metadata.indexable,
    });
    return { ...built, metadata: applyFacetsToMetadata(built.metadata, analysis, ctx) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, inventory, directory, cities, searchParams.toString(), ctx.environment, ctx.siteUrl]);

  // Unknown slug, or a legacy alias that has a different canonical spelling.
  if (!category) return <Navigate to={ROUTES.serviceIndex()} replace />;
  if (category.slug !== serviceSlug) return <Navigate to={ROUTES.service(category.slug)} replace />;

  return <PublicSeoPage model={model} loading={inventoryLoading || directoryLoading} />;
}

export default ServiceHubPage;
